import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {numberD, numberToCurrency} from 'src/smoothr-web-app-core/utils/utils';
import Order from '../../../smoothr-web-app-core/models/Order';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AddCustomTipComponent } from '../add-custom-tip/add-custom-tip.component';
import {OrderType} from "../../../smoothr-web-app-core/enums/OrderType";
import {PreorderType} from "../../../smoothr-web-app-core/enums/PreorderType";
import Venue from "../../../smoothr-web-app-core/models/Venue";
import {Company} from "../../../smoothr-web-app-core/models/CompanyCode";

export class Tip {
	orderType: OrderType;
	subOrderTypes: PreorderType[]; // f.e. preOrder or preOrder.delivery or terminal
	enabled: boolean;
	defaultTips: DefaultTip[];
}

export interface DefaultTip {
	value: number; // positive number (2 decimal places)
	type: TipsEnum;
	isDefault?: boolean;
}
export enum TipsEnum {
	ABSOLUTE = 'absolute',
	RELATIVE = 'relative',
	ANY = 'any',
}


@Component({
	selector: 'app-tip',
	templateUrl: './tip.component.html',
	styleUrls: ['tip.component.scss'],
})
export class TipComponent implements OnInit {
	@Output() tip = new EventEmitter<number>();

	percents = [5, 10, 15];
	tipsValues: DefaultTip[] = [];
	numberToCurrency = numberToCurrency;

	constructor(
		private modalCtrl: ModalController,
		private translate: TranslateService
	) {}
	public ifTipsEnabled = false;
	public customTipsEnter = false;
	private _totalPrice: number;
	private _venue: Venue;
	get totalPrice(): number {
		return this._totalPrice;
	}

	set totalPrice(value: number) {
		if (this._totalPrice === value) {
			return;
		}
		this._totalPrice = value;
		this.recalculateAndEmitTip();
	}

	private _order: Order;
	private _companyCode: Company | null = null;

	get order(): Order {
		return this._order;
	}
	@Input()
	set venue(venue: Venue) {
		this._venue = venue;
	}
	@Input()
	set order(order: Order) {
		this._order = order;
		this.recalculateTotalPrice();
	}

	@Input()
	set companyCode(companyCode: Company | null) {
		this._companyCode = companyCode;
		this.recalculateTotalPrice();
	}

	get currency(): string {
		return this.order && this.order.currency;
	}

	private _customTip: number;

	get customTip(): number {
		return this._customTip;
	}

	set customTip(value: number) {
		this._customTip = value;
		this.recalculateAndEmitTip();
	}

	private _selectedPercent: DefaultTip = null;

	get selectedPercent(): DefaultTip {
		return this._selectedPercent;
	}

	set selectedPercent(percent: DefaultTip) {
		this._selectedPercent = percent;
		this.recalculateAndEmitTip();
	}

	ngOnInit() {
		this.checkIfAvailable();
	}

	private recalculateTotalPrice() {
		if (!this._order) {
			this.totalPrice = 0;
			return;
		}
		const companyCode = this._order?.preorder?.type === PreorderType.FOOD_SPOT ? this._companyCode : null;
		this.totalPrice = OrderUtils.orderTotalPrice(this._order, true, true, companyCode);
	}

	async showCustomPicker(): Promise<number> {
		let customTipValue =
			this.customTip && this.customTip.toString().includes('.')
				? this.customTip.toString().replace('.', ',')
				: this.customTip
					? this.customTip.toString()
					: '';
		let result = await AddCustomTipComponent.show(this.modalCtrl, customTipValue);
		if (!result) {
			return -1;
		}
		const selectedValue = Number(result.toString().replace(',', '.'));
		this.customTip = selectedValue;
		this.selectedPercent = null;
		return selectedValue;
	}
	private checkIfAvailable() {
		console.log('checkIfAvailable', this._venue, this._order);

		if (this.ifTipsEnabled || !this._venue || !this._order) return;

		try {
			const { tips } = this._venue;
			const order = this._order;

			if (!tips?.length) return;

			const availableTips = tips.filter(
				tip => tip.enabled && tip.orderType === order.type && tip.subOrderTypes.includes(order.preorder.type)
			);

			if (!availableTips.length) return;

			const defaultTips = availableTips[0].defaultTips || [];

			this.tipsValues = defaultTips.filter(tip => {
				if (!tip.value) tip.type = TipsEnum.ANY;
				if (tip.type === TipsEnum.ANY) {
					this.customTipsEnter = true;
					return false;
				}
				return true;
			});

			this.ifTipsEnabled = true;

			this.tipsValues.forEach(tip => {
				if (tip.isDefault) this.select(tip);
			});

			//if (this.tipsValues?.[0]?.value) this.tipsValues = [{ type: TipsEnum.ANY, value: 0 }, ...this.tipsValues];
		} catch (e) {
			this.ifTipsEnabled = false;
			this.tipsValues = [];
		}
	}
	recalculateAndEmitTip() {
		if (this.customTip) {
			this.tip.emit(this.customTip);
			return;
		}
		// console.log(this.selectedPercent?.value.$numberDecimal);
		// console.log(numberD(this.selectedPercent?.value) * 100);
		if (this.selectedPercent && this.selectedPercent?.value) {
			const customTipsCalc =
				this.selectedPercent?.type === TipsEnum.RELATIVE
					? +(this.totalPrice * (numberD(this.selectedPercent.value) * 100) * 0.01).toFixed(2)
					: numberD(this.selectedPercent.value);
			this.tip.emit(customTipsCalc);
			return;
		}

		this.tip.emit(0);
		return;
	}

	select(percent: DefaultTip) {
		try {
			if (this.selectedPercent?.type === percent?.type && numberD(this.selectedPercent?.value) === numberD(percent.value)) {
				this.selectedPercent = null;
			} else {
				this.selectedPercent = percent;
			}
			this.customTip = 0;
		} catch (e) {
			this.customTip = 0;
		}
	}

	deleteTips() {
		this.selectedPercent = null;
		this.tip.emit(0);
	}

	public mapTipsEnumToValue(data: DefaultTip): string {
		if (data.type === TipsEnum.ANY && data.value === 0) {
			return this.translate.instant('tip_component.no_tip');
		}

		if (!data.value) return '';

		if (data.type === TipsEnum.RELATIVE) {
			return numberToCurrency(numberD(data.value) * this.totalPrice, this.order.currency)
		}
		const currency = this._order.currency;
		if (!currency) {
			return '' + numberD(data);
		}
		return numberToCurrency(numberD(data.value), this.order.currency)
	}
}
